import { useState, type ReactNode } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Link2Off, WifiOff } from "lucide-react";
import { Input } from "@/shared/components/Input";
import { CampoDataNascimento } from "@/shared/components/CampoDataNascimento";
import { Button } from "@/shared/components/Button";
import { AuthCardLayout } from "@/shared/components/AuthCardLayout";
import { useCadastroPublico, useIgrejaDoCadastro } from "@/core/hooks/useUsuarios";
import { cpfEhValido, sanitizarCpf } from "@/core/utils/cpf";
import { dataHojeInput } from "@/shared/lib/datas";
import { CampoSexo } from "@/shared/components/CampoSexo";

// F2-41 (pedido do usuário, 26/07/2026): data de nascimento passa a ser
// obrigatória para toda pessoa — usada na lista "Aniversariantes da Semana"
// da tela Início (só dia/mês aparecem lá, nunca idade ou ano).
const cadastroSchema = z.object({
  cpf: z
    .string()
    .min(1, "Informe seu CPF.")
    .refine((v) => cpfEhValido(v), "CPF inválido. Use apenas os 11 números."),
  nomeCompleto: z.string().min(3, "Informe seu nome completo."),
  telefone: z.string().optional(),
  email: z.string().email("E-mail inválido."),
  dataNascimento: z
    .string()
    .min(1, "Informe sua data de nascimento.")
    .refine((v) => v <= dataHojeInput(), "A data de nascimento não pode ser no futuro."),
  // Sexo (02/11/2026): obrigatório, duas opções. Nada vem marcado de início.
  sexo: z.enum(["masculino", "feminino"], { errorMap: () => ({ message: "Informe o sexo." }) }),
  senha: z.string().min(6, "Senha precisa ter ao menos 6 caracteres."),
});

type CadastroFormData = z.infer<typeof cadastroSchema>;

/** Ícone redondo e suave usado nos avisos da tela (link inválido, falha de conexão). */
function IconeAviso({ children }: { children: ReactNode }) {
  return (
    <div
      className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"
      aria-hidden="true"
    >
      {children}
    </div>
  );
}

export function CadastroPublico() {
  const navigate = useNavigate();
  const cadastro = useCadastroPublico();
  // Link por igreja: /cadastro/<codigo>. Sem código (link antigo /cadastro) o banco só
  // resolve se existir uma única igreja ativa. Visão SaaS futura: docs/oficial/arquitetura.md (seção 9).
  const { codigo } = useParams<{ codigo?: string }>();
  const igreja = useIgrejaDoCadastro(codigo);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CadastroFormData>({ resolver: zodResolver(cadastroSchema) });

  async function onSubmit(dados: CadastroFormData) {
    setErro(null);
    try {
      await cadastro.mutateAsync({
        dados: {
          cpf: sanitizarCpf(dados.cpf),
          nomeCompleto: dados.nomeCompleto,
          telefone: dados.telefone,
          email: dados.email,
          dataNascimento: dados.dataNascimento,
          sexo: dados.sexo,
          senha: dados.senha,
        },
        codigoIgreja: codigo,
      });
      setSucesso(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Erro ao cadastrar. Tente novamente.");
    }
  }

  if (sucesso) {
    return (
      <AuthCardLayout titulo="Cadastro recebido!">
        <p className="text-center text-sm text-muted">
          Seu cadastro foi enviado para aprovação. Você poderá fazer login assim que um
          administrador liberar seu acesso.
        </p>
        <Button onClick={() => navigate("/login")} className="mt-6 w-full">
          Voltar para o login
        </Button>
      </AuthCardLayout>
    );
  }

  if (igreja.isLoading) {
    return (
      <AuthCardLayout titulo="Criar conta">
        <p className="text-center text-sm text-muted">Verificando o link…</p>
      </AuthCardLayout>
    );
  }

  // Falha ao consultar (internet, servidor): não é culpa do link, então não diz que ele é inválido.
  if (igreja.isError) {
    return (
      <AuthCardLayout titulo="Não foi possível verificar o link">
        <IconeAviso>
          <WifiOff size={26} />
        </IconeAviso>
        <p className="text-center text-sm text-muted">
          Parece que houve um problema de conexão. Confira a sua internet e tente de novo.
        </p>
        <Button onClick={() => void igreja.refetch()} carregando={igreja.isFetching} className="mt-6 w-full">
          Tentar de novo
        </Button>
        <p className="mt-4 text-center text-sm text-muted">
          <Link to="/login" className="font-bold text-primary hover:underline">
            Voltar para o login
          </Link>
        </p>
      </AuthCardLayout>
    );
  }

  if (!igreja.data) {
    return (
      <AuthCardLayout titulo="Não encontramos este link">
        <IconeAviso>
          <Link2Off size={26} />
        </IconeAviso>
        <p className="text-center text-sm text-muted">
          O endereço pode estar incompleto, ter um erro de digitação ou ter sido trocado. Peça à
          liderança da sua igreja o link de cadastro atualizado.
        </p>
        <Button onClick={() => navigate("/login")} className="mt-6 w-full">
          Voltar para o login
        </Button>
        <p className="mt-4 text-center text-sm text-muted">Já tem conta? Entre pelo login.</p>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout
      titulo="Criar conta"
      subtitulo={`${igreja.data.nome} — seu acesso precisa ser aprovado por um administrador antes do 1º login`}
      rodape={
        <>
          Já tem conta?{" "}
          <Link to="/login" className="font-bold text-primary hover:underline">
            Entrar
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <Input
          label="CPF"
          placeholder="000.000.000-00"
          inputMode="numeric"
          erro={errors.cpf?.message}
          {...register("cpf")}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 11);
            e.target.value = value;
          }}
        />
        <Input
          label="Nome completo"
          erro={errors.nomeCompleto?.message}
          {...register("nomeCompleto")}
        />
        <Input
          label="Telefone"
          placeholder="(00) 00000-0000"
          inputMode="tel"
          erro={errors.telefone?.message}
          {...register("telefone")}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 11);
            e.target.value = value;
          }}
        />
        <Input label="E-mail" type="email" erro={errors.email?.message} {...register("email")} />
        <Controller
          name="dataNascimento"
          control={control}
          render={({ field }) => (
            <CampoDataNascimento
              label="Data de nascimento"
              name={field.name}
              ref={field.ref}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              erro={errors.dataNascimento?.message}
            />
          )}
        />
        <Controller
          name="sexo"
          control={control}
          render={({ field }) => (
            <CampoSexo valor={field.value ?? ""} onChange={field.onChange} erro={errors.sexo?.message} />
          )}
        />
        <Input label="Senha" type="password" erro={errors.senha?.message} {...register("senha")} />

        {erro && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
            {erro}
          </p>
        )}

        <Button type="submit" carregando={isSubmitting} className="mt-1">
          Criar conta
        </Button>
      </form>
    </AuthCardLayout>
  );
}
